/**
 * Convert LCH to RGB string
 * @param l
 * @param c
 * @param h
 * @param a
 * @returns {string}
 */

function rgbToString ({ r, g, b, a}) {
  return `rgb(${r}, ${g}, ${b})`
}

export default function lchToRgbString ({ l, c, h, a = 100 }) {
  [l, c, h] = force_into_gamut(l, c, h)

  return 'rgb(' + LCH_to_sRGB([+l, +c, +h]).map(x => {
    return Math.round(x * 10000) / 100 + '%'
  }).join(' ') + alpha_to_string(a) + ')'
}


function alpha_to_string (a = 100) {
  return (a < 100 ? ` / ${a}%` : '')
}

function force_into_gamut (l, c, h) {
  if (isLCH_within_sRGB(l, c, h)) {
    return [l, c, h]
  }

  let hiC = c
  let loC = 0
  const x = .0001
  c /= 2

  // .0001 chosen fairly arbitrarily as "close enough"
  while (hiC - loC > x) {
    if (isLCH_within_sRGB(l, c, h)) {
      loC = c
    } else {
      hiC = c
    }
    c = (hiC + loC) / 2
  }

  return [l, c, h]
}

function isLCH_within_sRGB (l, c, h) {
  var rgb = LCH_to_sRGB([+l, +c, +h])
  const x = .000005
  return rgb.reduce((a, b) => a && b >= (0 - x) && b <= (1 + x), true)
}

function LCH_to_sRGB (LCH) {
  return gam_sRGB(XYZ_to_lin_sRGB(D50_to_D65(Lab_to_XYZ(LCH_to_Lab(LCH)))))
}

function LCH_to_Lab (LCH) {
  return [
    LCH[0], // L
    LCH[1] * Math.cos(LCH[2] * Math.PI / 180), // a
    LCH[1] * Math.sin(LCH[2] * Math.PI / 180) // b
  ]
}

function Lab_to_XYZ (Lab) {
  // Convert Lab to D50-adapted XYZ
  // http://www.brucelindbloom.com/index.html?Eqn_RGB_XYZ_Matrix.html
  var x = 24389 / 27   // 29^3/3^3
  var y = 216 / 24389  // 6^3/29^3
  var white = [0.96422, 1.00000, 0.82521] // D50 reference white
  var f = []

  // compute f, starting with the luminance-related term
  f[1] = (Lab[0] + 16) / 116
  f[0] = Lab[1] / 500 + f[1]
  f[2] = f[1] - Lab[2] / 200

  // compute xyz
  var xyz = [
    Math.pow(f[0], 3) > y ? Math.pow(f[0], 3) : (116 * f[0] - 16) / x,
    Lab[0] > x * y ? Math.pow((Lab[0] + 16) / 116, 3) : Lab[0] / x,
    Math.pow(f[2], 3) > y ? Math.pow(f[2], 3) : (116 * f[2] - 16) / x
  ]

  // Compute XYZ by scaling xyz by reference white
  return xyz.map((value, i) => value * white[i])
}

function D50_to_D65 (XYZ) {
  // Bradford chromatic adaptation from D50 to D65
  var M = [
    [0.9555766, -0.0230393, 0.0631636],
    [-0.0282895, 1.0099416, 0.0210077],
    [0.0122982, -0.0204830, 1.3299098]
  ]

  return multiplyMatrices(M, XYZ)
}

function XYZ_to_lin_sRGB (XYZ) {
  // convert XYZ to linear-light sRGB

  var M = [
    [3.2409699419045226, -1.537383177570094, -0.4986107602930034],
    [-0.9692436362808796, 1.8759675015077202, 0.04155505740717559],
    [0.05563007969699366, -0.20397695888897652, 1.0569715142428786]
  ]

  return multiplyMatrices(M, XYZ)
}

function gam_sRGB (RGB) {
  // convert an array of linear-light sRGB values in the range 0.0-1.0
  // to gamma corrected form
  // https://en.wikipedia.org/wiki/SRGB
  // For negative values, linear portion extends on reflection
  // of axis, then uses reflected pow below that
  return RGB.map(function (val) {
    let sign = val < 0 ? -1 : 1
    let abs = Math.abs(val)

    if (abs > 0.0031308) {
      return sign * (1.055 * Math.pow(abs, 1 / 2.4) - 0.055)
    }

    return 12.92 * val
  })
}

/**
 * Simple matrix (and vector) multiplication
 * Warning: No error handling for incompatible dimensions!
 * @author Lea Verou 2020 MIT License
 */
// A is m x n. B is n x p. product is m x p.
function multiplyMatrices (A, B) {
  let m = A.length

  if (!Array.isArray(A[0])) {
    // A is vector, convert to [[a, b, c, ...]]
    A = [A]
  }

  if (!Array.isArray(B[0])) {
    // B is vector, convert to [[a], [b], [c], ...]]
    B = B.map(x => [x])
  }

  let p = B[0].length
  let B_cols = B[0].map((_, i) => B.map(x => x[i])) // transpose B
  let product = A.map(row => B_cols.map(col => {
    if (!Array.isArray(row)) {
      return col.reduce((a, c) => a + c * row, 0)
    }

    return row.reduce((a, c, i) => a + c * (col[i] || 0), 0)
  }))

  if (m === 1) {
    product = product[0] // Avoid [[a, b, c, ...]]
  }

  if (p === 1) {
    return product.map(x => x[0]) // Avoid [[a], [b], [c], ...]]
  }

  return product
}
